import React, { useState } from 'react'
import { Infinity as InfinityIcon, Pencil } from 'lucide-react'
import { useTheme } from '@/context/ThemeContext'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'

type Props = {
  membershipId: string
  memberName: string
  allocation: number | null
  used: number
  canEdit: boolean
  onSave: (membershipId: string, allocation: number | null) => Promise<void>
  error?: string
}

const AllocationCell: React.FC<Props> = ({ membershipId, memberName, allocation, used, canEdit, onSave, error }) => {
  const { currentTheme: t } = useTheme()
  const [open, setOpen] = useState(false)
  const [unlimited, setUnlimited] = useState(allocation === null)
  const [draft, setDraft] = useState<string>(allocation === null ? '' : String(allocation))
  const [saving, setSaving] = useState(false)

  const percentUsed = allocation === null ? null : Math.min((used / Math.max(allocation, 1)) * 100, 100)
  const isWarning = percentUsed !== null && percentUsed >= 90

  const openEdit = () => {
    setUnlimited(allocation === null)
    setDraft(allocation === null ? '' : String(allocation))
    setOpen(true)
  }

  const handleOpenChange = (next: boolean) => {
    if (!saving) setOpen(next)
  }

  const confirmEdit = async () => {
    setSaving(true)
    try {
      const value = unlimited ? null : Math.max(0, Number(draft) || 0)
      await onSave(membershipId, value)
      setOpen(false)
    } catch {
      // Error surfaced below the field via the `error` prop; keep the dialog open.
    } finally {
      setSaving(false)
    }
  }

  return (
    <>
      <div className="flex flex-col items-end gap-1 min-w-[130px]">
        <div className="flex items-center gap-1.5">
          {allocation === null ? (
            <span
              className="inline-flex items-center gap-1 text-[11.5px] font-semibold px-2.5 py-[3px] rounded-full"
              style={{ backgroundColor: t.hoverBackground, color: t.mutedText }}
            >
              <InfinityIcon size={11} />
              Unlimited
            </span>
          ) : (
            <span className="text-[12.5px] font-medium" style={{ color: t.text }}>
              {used.toLocaleString()} / {allocation.toLocaleString()}
            </span>
          )}
          {canEdit && (
            <button
              type="button"
              onClick={openEdit}
              aria-label="Edit allocation"
              className="w-5 h-5 rounded-lg flex items-center justify-center transition-colors"
              style={{ color: t.mutedText }}
              onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = t.hoverBackground }}
              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent' }}
            >
              <Pencil size={11} />
            </button>
          )}
        </div>
        {allocation !== null && (
          <div className="w-full max-w-[110px] h-[5px] rounded-full overflow-hidden" style={{ backgroundColor: t.borderSecondary }}>
            <div
              className="h-full rounded-full transition-[width]"
              style={{
                width: `${percentUsed ?? 0}%`,
                backgroundColor: isWarning ? t.warningText : t.accent,
              }}
            />
          </div>
        )}
      </div>

      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent
          className="py-6 px-7 rounded-2xl shadow-xl max-w-sm w-full border-none"
          style={{ background: t.secondary, color: t.text }}
        >
          <DialogHeader>
            <DialogTitle className="text-base font-semibold" style={{ color: t.text }}>
              Credit allocation
            </DialogTitle>
          </DialogHeader>
          <p className="text-[13px] -mt-1" style={{ color: t.mutedText }}>
            Set {memberName}&rsquo;s monthly AI credit limit for this workspace.
          </p>

          <div className="flex mt-4 rounded-lg p-[3px]" style={{ backgroundColor: t.sectionBackground, border: `1px solid ${t.border}` }}>
            <button
              type="button"
              onClick={() => setUnlimited(true)}
              disabled={saving}
              className="flex-1 rounded-md py-2 text-[13px] font-semibold transition-colors"
              style={unlimited
                ? { background: t.accentGradient ?? t.accent, color: '#FFFFFF' }
                : { color: t.mutedText }}
            >
              Unlimited
            </button>
            <button
              type="button"
              onClick={() => setUnlimited(false)}
              disabled={saving}
              className="flex-1 rounded-md py-2 text-[13px] font-semibold transition-colors"
              style={!unlimited
                ? { background: t.accentGradient ?? t.accent, color: '#FFFFFF' }
                : { color: t.mutedText }}
            >
              Limited
            </button>
          </div>

          {!unlimited && (
            <div className="mt-3">
              <input
                type="number"
                min={0}
                autoFocus
                disabled={saving}
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                placeholder="Monthly credits"
                className="w-full h-[38px] rounded-lg px-3 text-[13.5px] outline-none"
                style={{ backgroundColor: t.sectionBackground, border: `1px solid ${t.border}`, color: t.text }}
              />
              <p className="text-[11.5px] mt-1.5" style={{ color: t.mutedText }}>
                {used.toLocaleString()} credits used so far this period.
              </p>
            </div>
          )}

          {error && (
            <p className="text-[12px] mt-3" style={{ color: t.errorText }}>{error}</p>
          )}

          <DialogFooter className="flex justify-end gap-2 mt-6">
            <Button
              variant="secondary"
              onClick={() => setOpen(false)}
              disabled={saving}
              className="px-4 py-2 rounded-lg font-medium h-auto text-sm"
              style={{ background: t.hoverBackground, color: t.text, border: `1px solid ${t.border}` }}
            >
              Cancel
            </Button>
            <Button
              onClick={confirmEdit}
              disabled={saving}
              className="px-4 py-2 rounded-lg font-medium h-auto text-sm border-none"
              style={{ background: t.accentGradient ?? t.accent, color: '#FFFFFF' }}
            >
              {saving ? 'Saving…' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}

export default React.memo(AllocationCell)
